import { baseApi } from './base.api';
import { API_VERSION, PATH_CONFIGURATION_API } from '../constants/api-routes';
import { getApiVersion } from 'src/utils/apiVersion';

type Id = number | string;

// Response shapes are only partially specified by the API; extra fields pass through untyped.
export interface ConfigurationSettings {
  id: number;
  version?: number;
  is_deployed?: boolean;
  is_custom?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface ConfigurationSettingsPage {
  items: ConfigurationSettings[];
  page_info: {
    page?: number;
    total_items: number;
    has_next_page?: boolean;
    has_previous_page?: boolean;
  };
}

// A field of a configuration template or prompt. `default_value.value` holds a string, boolean or
// workflow depending on the field.
export interface ConfigurationFormField<Value = unknown> {
  key: string;
  type?: string;
  is_required?: boolean;
  options?: ConfigurationFormFieldOption[];
  default_value?: { key?: string; value?: Value };
  [key: string]: unknown;
}

export interface ConfigurationFormFieldOption {
  key: string;
  value: string;
  [key: string]: unknown;
}

export interface ConfigurationPrompt {
  id: number;
  template_id?: number;
  // Manual (free-text system prompt) versions have no form fields.
  is_manual?: boolean;
  // The prompt text of a manual version.
  text?: string;
  form_fields?: ConfigurationFormField[];
  // Placeholder name -> value; persona prompts hold the pf_* fields.
  input_data?: Record<string, unknown>;
  [key: string]: unknown;
}

// An account's custom system prompt template; prompt_parts[0] holds its text and the
// input_data key the text is stored under.
export interface CustomTemplate {
  id: number;
  name: string;
  prompt_parts: { placeholder: string; template: string }[];
  [key: string]: unknown;
}

export interface ConfigurationTemplateFields {
  id: number;
  form_fields?: ConfigurationFormField[];
  [key: string]: unknown;
}

export const configurationService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      // Callers pass either {} or nothing; both are kept as distinct cache keys.
      getDefaultConfigurationTemplateFields: build.query<
        ConfigurationTemplateFields,
        Record<string, never> | void
      >({
        query: () => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_CONFIGURATION_API.DEFAULT_TEMPLATE_FIELDS}`,
          };
        },
        providesTags: ['defaultTemplateFields'],
      }),
      getCustomTemplates: build.query<CustomTemplate[], void>({
        query: () => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_CONFIGURATION_API.CUSTOM_TEMPLATES}`,
          };
        },
        providesTags: ['customTemplates'],
      }),
      getAllConfigurationSettings: build.query<
        ConfigurationSettingsPage,
        { id: Id; page?: number; page_size?: number }
      >({
        query: ({ id, page = 1, page_size = 100 }) => {
          const version = getApiVersion();
          const ROOT =
            version === API_VERSION.V1
              ? PATH_CONFIGURATION_API.ROOT_PROJECT
              : PATH_CONFIGURATION_API.ROOT_PROJECTS;

          return {
            url: `${version}/${ROOT}/${id}/settings?page=${page}&page_size=${page_size}`,
          };
        },
        providesTags: ['ConfigurationSettings'],
      }),
      createConfigurationSettings: build.mutation<
        ConfigurationSettings,
        { id: Id; body: Record<string, unknown> }
      >({
        query: ({ id, body }) => {
          const version = getApiVersion();
          const ROOT =
            version === API_VERSION.V1
              ? PATH_CONFIGURATION_API.ROOT_PROJECT
              : PATH_CONFIGURATION_API.ROOT_PROJECTS;

          return {
            url: `${version}/${ROOT}/${id}/settings`,
            method: 'POST',
            body,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: settings } = await queryFulfilled;
            const page = 1;

            dispatch(
              configurationService.util.updateQueryData(
                'getAllConfigurationSettings',
                { id, page },
                (draft) => {
                  draft.page_info.total_items = draft.page_info.total_items + 1;
                  draft.items.unshift(settings);
                },
              ),
            );
          } catch {}
        },
      }),
      deployConfigurationSettings: build.mutation<
        unknown,
        { project_id: Id; settings_id: Id; body?: Record<string, unknown> }
      >({
        query: ({ project_id, settings_id, body = {} }) => {
          const version = getApiVersion();
          const ROOT =
            version === API_VERSION.V1
              ? PATH_CONFIGURATION_API.ROOT_PROJECT
              : PATH_CONFIGURATION_API.ROOT_PROJECTS;

          return {
            url: `${version}/${ROOT}/${project_id}/settings/${settings_id}/deploy`,
            method: 'POST',
            body,
          };
        },
        invalidatesTags: ['ConfigurationSettings'],
      }),
      getConfigurationSettingsPrompt: build.query<
        ConfigurationPrompt,
        { project_id: Id; settings_id: Id }
      >({
        query: ({ project_id, settings_id }) => {
          const version = getApiVersion();
          const ROOT =
            version === API_VERSION.V1
              ? PATH_CONFIGURATION_API.ROOT_PROJECT
              : PATH_CONFIGURATION_API.ROOT_PROJECTS;

          return {
            url: `${version}/${ROOT}/${project_id}/settings/${settings_id}/prompt`,
          };
        },
        providesTags: ['ConfigurationSettingsPrompt'],
      }),
      updateConfigurationSettingsPrompt: build.mutation<
        ConfigurationPrompt,
        { project_id: Id; settings_id: Id; body: Record<string, unknown> }
      >({
        query: ({ project_id, settings_id, body }) => {
          const version = getApiVersion();
          const ROOT =
            version === API_VERSION.V1
              ? PATH_CONFIGURATION_API.ROOT_PROJECT
              : PATH_CONFIGURATION_API.ROOT_PROJECTS;

          return {
            url: `${version}/${ROOT}/${project_id}/settings/${settings_id}/prompt`,
            method: 'PUT',
            body,
          };
        },
        async onQueryStarted({ project_id, settings_id }, { dispatch, queryFulfilled }) {
          try {
            const { data: settings } = await queryFulfilled;
            dispatch(
              configurationService.util.updateQueryData(
                'getConfigurationSettingsPrompt',
                { project_id, settings_id },
                (draft) => {
                  Object.assign(draft, settings);
                },
              ),
            );
          } catch {}
        },
      }),
    };
  },
});

export const {
  useGetDefaultConfigurationTemplateFieldsQuery,
  useGetAllConfigurationSettingsQuery,
  useGetCustomTemplatesQuery,
  useGetConfigurationSettingsPromptQuery,
  useLazyGetConfigurationSettingsPromptQuery,
  useCreateConfigurationSettingsMutation,
  useDeployConfigurationSettingsMutation,
  useUpdateConfigurationSettingsPromptMutation,
} = configurationService;
